/**
 * applyAuthSession
 * ----------------
 * Single source of truth for "the user is now logged in."
 *
 *   - `applyAuthSessionClient` — used by password login (`/login`) and
 *     SSO complete (`/sso/complete`). Both paths call this after
 *     `/v1/user_authentication/authenticate` or `/v1/sso/validate`
 *     (same response shape), so cookies match exactly.
 *   - `applyAuthSessionServer` — optional NextResponse helper if a
 *     route handler must set cookies without a client round-trip.
 *     Cookies are host-only so environments do not share sessions.
 */

import type { NextResponse } from "next/server";
import {
  AUTH_SESSION_COOKIE_KEYS,
  clearAuthCookies,
  clearAuthCookiesOnResponse,
  getAuthCookieOptions,
  setAuthCookie,
} from "@/lib/authCookies";

export type AuthSession = {
  token?: string;
  ca_user_id?: string | number;
  company_id?: string | number;
  company_name?: string;
  company_user_id?: string | number;
  user_type?: string;
  user_type_id?: string | number;
  user_pay_type?: string;
  user_role?: string;
  user_role_id?: string | number;
  user_full_name?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  password_expiry_dtm?: string;
  locked_out_flag?: boolean | string;
  email?: string;
  ca_user_active_flag?: boolean | string;
  company_user_active_flag?: boolean | string;
  user_company_association_flag?: boolean | string;
  sns_id?: string;
  message?: string;
  status?: string;
  session_timeout_minutes?: number | string;
  signature?: string;
  profile_picture_url?: string;
  redirect_to?: string;
  [key: string]: unknown;
};

const COOKIE_KEYS = AUTH_SESSION_COOKIE_KEYS;

const stringify = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
};

/**
 * Apply the cookie set on the client (used by the password-login flow).
 * Mirrors the inline `setCookie(...)` block that lived in
 * `src/app/login/page.tsx` before this helper was extracted.
 */
export function applyAuthSessionClient(session: AuthSession): void {
  clearAuthCookies();

  for (const key of COOKIE_KEYS) {
    setAuthCookie(key, stringify(session[key]));
  }

  setAuthCookie("isAuthenticated", "true");

  if (typeof window !== "undefined") {
    const profilePictureUrl =
      typeof session.profile_picture_url === "string"
        ? session.profile_picture_url
        : "";
    try {
      window.localStorage.setItem("user_avatar", profilePictureUrl);
    } catch {
      /* swallow */
    }

    try {
      const keys = Object.keys(window.localStorage);
      for (const k of keys) {
        if (k.startsWith("ca-logout-")) {
          window.localStorage.removeItem(k);
        }
      }
    } catch {
      /* swallow */
    }

    const caUserId = session.ca_user_id;
    if (caUserId !== undefined && caUserId !== null && caUserId !== "") {
      try {
        window.sessionStorage.setItem("just-logged-in", "true");
        window.localStorage.setItem(
          `ca-login-${caUserId}`,
          Date.now().toString(),
        );
      } catch {
        /* swallow */
      }
    }
  }
}

/**
 * Apply the cookie set on a server-side NextResponse (used by the SSO
 * ACS Route Handler). Match the password-login cookie set: same names,
 * host-only cookie options, plus `isAuthenticated=true`. Marked
 * `secure: true` outside dev because the SAML POST always arrives over HTTPS.
 */
export function applyAuthSessionServer(
  res: NextResponse,
  session: AuthSession,
  hostname?: string,
): NextResponse {
  clearAuthCookiesOnResponse(res, hostname);

  const isProd = process.env.NODE_ENV === "production";
  const cookieOptions = getAuthCookieOptions(hostname, isProd);

  for (const key of COOKIE_KEYS) {
    res.cookies.set({
      name: key,
      value: stringify(session[key]),
      ...cookieOptions,
    });
  }

  res.cookies.set({
    name: "isAuthenticated",
    value: "true",
    ...cookieOptions,
  });

  return res;
}
